import { Injectable } from '@angular/core';
import { App, AppState, URLOpenListenerEvent } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { createEffect } from '@ngrx/effects';
import { fromEventPattern, map } from 'rxjs';

import { capacitorAppActions } from './capacitor-app.actions';

/**
 * The one place `@capacitor/app` events are listened for, so no consumer has to import the plugin or
 * hold a listener open across a component's life.
 *
 * The dispatcher decides nothing: it does not filter, gate on platform, or derive a higher-level "the
 * app came back" meaning. Each of those is a decision one consumer makes and another would have to
 * undo, so each belongs in the consumer.
 *
 * `fromEventPattern` rather than a manual subscription ties the listener's lifetime to the effect's:
 * registered once when the effect is subscribed, removed on teardown. The teardown receives whatever
 * the registration returned, and `addListener` returns a **promise** of a handle, which has to be
 * awaited before `remove()` exists on it — calling `.remove()` on the promise is `undefined()`, a
 * `TypeError` during unsubscribe that RxJS wraps as `UnsubscriptionError`, leaving the listener
 * attached.
 */
@Injectable()
export class CapacitorAppEffects {
  readonly appResumed$ = createEffect(() =>
    fromEventPattern<void>(
      handler => App.addListener('resume', handler),
      (_handler, handle: Promise<PluginListenerHandle>) => void handle?.then(listener => listener.remove())
    ).pipe(map(() => capacitorAppActions.appResumed()))
  );

  readonly appStateChanged$ = createEffect(() =>
    fromEventPattern<AppState>(
      handler => App.addListener('appStateChange', handler),
      (_handler, handle: Promise<PluginListenerHandle>) => void handle?.then(listener => listener.remove())
    ).pipe(map(({ isActive }) => capacitorAppActions.appStateChanged({ isActive })))
  );

  /**
   * One listener for every deep link, which is what makes a cold-start link deterministic.
   *
   * Both platforms retain `appUrlOpen` until it is consumed and then deliver it to the **first**
   * listener to register — so while two listeners existed, a launch-time link went to whichever
   * registration won, and the other consumer saw nothing.
   *
   * The retained event now reaches its consumers through the action stream, which is hot: an action
   * dispatched before a consumer subscribes is gone. That holds because the delivery costs a bridge
   * round trip, and every consumer is registered eagerly under `PlatformStateModule` — so they are all
   * subscribed within the same module-init turn that registers this listener. A consumer reached only
   * from a lazy route could not rely on it.
   */
  readonly appUrlOpened$ = createEffect(() =>
    fromEventPattern<URLOpenListenerEvent>(
      handler => App.addListener('appUrlOpen', handler),
      (_handler, handle: Promise<PluginListenerHandle>) => void handle?.then(listener => listener.remove())
    ).pipe(map(({ url }) => capacitorAppActions.appUrlOpened({ url })))
  );
}
